import { useMemo } from 'react';
import { BadgeCheck, Database, Shield, Users } from 'lucide-react';
import { useData } from './DataContext';
import { useI18n } from './I18nContext';
import { PREMIERSHIP_CLUBS, normalizeClubName } from './leagueClubs';

export function LeagueClubDirectory({
  selectedKey = 'polonia-glasgow',
  onSelectClub,
}: {
  selectedKey?: string;
  onSelectClub?: (clubKey: string) => void;
}) {
  const { t } = useI18n();
  const { leagueClubs } = useData();

  const databaseByName = useMemo(() => {
    const map = new Map<string, (typeof leagueClubs)[number]>();
    leagueClubs.forEach((club) => {
      map.set(normalizeClubName(club.canonical_name), club);
      map.set(normalizeClubName(club.display_name), club);
    });
    return map;
  }, [leagueClubs]);

  const selected =
    PREMIERSHIP_CLUBS.find((club) => club.key === selectedKey) ||
    PREMIERSHIP_CLUBS[0];
  const selectedDatabaseRow =
    databaseByName.get(normalizeClubName(selected.canonicalName)) ||
    databaseByName.get(normalizeClubName(selected.displayName));

  return (
    <section className="club-directory-section" id="league-club-sheet">
      <div className="section-title-line">
        <div>
          <p className="eyebrow">{t.clubIdentity}</p>
          <h2>{t.premiershipClubs}</h2>
        </div>
        <span>{PREMIERSHIP_CLUBS.length} {t.activeClubs}</span>
      </div>

      <div className="club-directory-layout">
        <div className="club-directory-grid">
          {PREMIERSHIP_CLUBS.map((club) => {
            const dbRow =
              databaseByName.get(normalizeClubName(club.canonicalName)) ||
              databaseByName.get(normalizeClubName(club.displayName));
            const active = club.key === selected.key;
            return (
              <button
                key={club.key}
                className={`club-directory-card ${active ? 'active' : ''}`}
                style={{
                  '--club-primary': club.primaryColor,
                  '--club-secondary': club.secondaryColor,
                } as React.CSSProperties}
                onClick={() => onSelectClub?.(club.key)}
              >
                <span className="club-card-glow" />
                <img src={club.logo} alt={club.displayName} />
                <strong>{club.shortName}</strong>
                <small>{club.displayName}</small>
                <i className={dbRow ? 'database-linked' : 'database-pending'}>
                  {dbRow ? <BadgeCheck size={15} /> : <Database size={15} />}
                </i>
              </button>
            );
          })}
        </div>

        <aside
          className="metal-card club-identity-detail"
          style={{
            '--club-primary': selected.primaryColor,
            '--club-secondary': selected.secondaryColor,
          } as React.CSSProperties}
        >
          <div className="club-identity-banner">
            <span />
            <img src={selected.logo} alt={selected.displayName} />
            <div>
              <p className="eyebrow">{t.selectedClub}</p>
              <h3>{selected.displayName}</h3>
              <small>{selected.canonicalName}</small>
            </div>
          </div>

          <dl className="club-detail-facts">
            <dt><Shield size={16} /> {t.clubKey}</dt>
            <dd>{selected.key}</dd>
            <dt><Database size={16} /> {t.databaseStatus}</dt>
            <dd className={selectedDatabaseRow ? 'status-good' : 'status-warning'}>
              {selectedDatabaseRow ? t.databaseLinked : t.databasePending}
            </dd>
            <dt><Users size={16} /> {t.aliases}</dt>
            <dd>{selected.aliases.join(' · ')}</dd>
          </dl>

          <div className="club-colour-strip">
            <span style={{ background: selected.primaryColor }} />
            <span style={{ background: selected.secondaryColor }} />
          </div>
        </aside>
      </div>
    </section>
  );
}
